import React, { useEffect, useState } from 'react';
import { CardSkeleton } from '../../../components/enterprise';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import CalculateIcon from '@mui/icons-material/Calculate';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import appManagementApi from '../../../services/appManagementApi';
import { useNotification } from '../../../contexts/NotificationContext';

/**
 * Operator-only page to configure how the RUM SDK install snippet is
 * rendered for customers.
 *
 * - **CDN URL**     where the customer's `<script src="">` points.
 * - **Beacon URL**  where the SDK posts events.
 * - **SRI hash**    integrity guard for the SDK file.
 * - **SDK version** display label.
 *
 * Empty values fall back to same-origin defaults — leave blank if you're
 * happy with whatever the platform vhost is serving.
 */
export default function RumPlatformSettingsPage() {
  const notification = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [form, setForm] = useState({
    sdkCdnUrl: '',
    beaconEndpoint: '',
    sdkSriHash: '',
    sdkVersion: '',
  });

  const sameOriginCdn =
    typeof window !== 'undefined'
      ? `${window.location.origin}/cdn/rum-sdk/v2/verops-rum.min.js`
      : '/cdn/rum-sdk/v2/verops-rum.min.js';
  const sameOriginBeacon =
    typeof window !== 'undefined' ? `${window.location.origin}/rum/v2/beacon` : '/rum/v2/beacon';

  useEffect(() => {
    let cancelled = false;
    appManagementApi
      .refreshPlatformSettings()
      .then((data) => {
        if (cancelled) return;
        setForm({
          sdkCdnUrl: data?.sdkCdnUrl || '',
          beaconEndpoint: data?.beaconEndpoint || '',
          sdkSriHash: data?.sdkSriHash || '',
          sdkVersion: data?.sdkVersion || '',
        });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await appManagementApi.updatePlatformSettings(form);
      notification.success('RUM platform settings saved.');
    } catch (e) {
      const detail = e?.response?.data?.message || e?.message || 'unknown error';
      notification.error(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({ sdkCdnUrl: '', beaconEndpoint: '', sdkSriHash: '', sdkVersion: '' });
  };

  // Recompute SRI hash server-side: backend fetches the SDK file from the
  // configured CDN URL, computes sha384, persists. No openssl-on-laptop step.
  //
  // The snackbar surfaces whether the hash actually changed (operator
  // could otherwise click and see no visible diff because their stored
  // hash already matched the deployed file). Bytes + URL are included
  // so operators can sanity-check that the backend reached the right
  // file — important when running multi-node staging where one node
  // could have a stale SDK on disk.
  const handleRecomputeSri = async () => {
    setRecomputing(true);
    try {
      const result = await appManagementApi.recomputeSri();
      const settings = result?.settings || {};
      setForm((f) => ({ ...f, sdkSriHash: settings.sdkSriHash || '' }));
      const newHash = settings.sdkSriHash || '';
      const shortHash = newHash ? `${newHash.slice(0, 22)}…${newHash.slice(-6)}` : '(empty)';
      const sizeKb = result?.fetchedBytes ? ` · ${(result.fetchedBytes / 1024).toFixed(1)} KB` : '';
      if (result?.changed === false) {
        notification.info(`SRI unchanged — deployed SDK already matches ${shortHash}${sizeKb}`);
      } else if (result?.changed === true) {
        notification.success(`SRI updated to ${shortHash}${sizeKb}`);
      } else {
        // Legacy backend (no changed flag) — fall back to a generic message.
        notification.success(`SRI recomputed: ${shortHash}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.error || e?.response?.data?.hint || e?.message || 'unknown error';
      notification.error(`Recompute failed: ${detail}`);
    } finally {
      setRecomputing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <Box sx={{ p: 3 }}><CardSkeleton count={2} /></Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto' }}>
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight={600}>
          RUM Platform Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          These values are interpolated into the install snippet shown to customers.
          Leave a field blank to fall back to the same-origin default served by the
          platform's Apache vhost.
        </Typography>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                  SDK CDN URL
                </Typography>
                <Typography variant="caption" color="text.secondary" mb={1} component="div">
                  Where customer browsers load the SDK from. Default: <code>{sameOriginCdn}</code>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={sameOriginCdn}
                  value={form.sdkCdnUrl}
                  onChange={onChange('sdkCdnUrl')}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                  Beacon Endpoint
                </Typography>
                <Typography variant="caption" color="text.secondary" mb={1} component="div">
                  Where the SDK POSTs RUM events. Default: <code>{sameOriginBeacon}</code>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={sameOriginBeacon}
                  value={form.beaconEndpoint}
                  onChange={onChange('beaconEndpoint')}
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                  SRI Integrity Hash
                </Typography>
                <Typography variant="caption" color="text.secondary" mb={1} component="div">
                  When set, the script tag in the install snippet pins this hash so browsers
                  refuse to execute a tampered SDK file. Click <strong>Recompute</strong> after
                  every SDK republish to fetch the file from the CDN URL above and hash it.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="sha384-..."
                    value={form.sdkSriHash}
                    onChange={onChange('sdkSriHash')}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={recomputing ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon />}
                    onClick={handleRecomputeSri}
                    disabled={recomputing}
                    sx={{ minWidth: 150 }}
                  >
                    {recomputing ? 'Hashing…' : 'Recompute'}
                  </Button>
                </Stack>
                <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                  The Recompute button fetches the SDK file server-side from the configured CDN
                  URL and writes the hash directly. No local openssl needed.
                </Alert>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                  SDK Version Label
                </Typography>
                <Typography variant="caption" color="text.secondary" mb={1} component="div">
                  Display-only — used in admin panels and audit log. Doesn't change behavior.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="v2.0.0"
                  value={form.sdkVersion}
                  onChange={onChange('sdkVersion')}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button startIcon={<RestoreIcon />} onClick={handleReset} disabled={saving}>
            Reset to defaults
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Save settings
          </Button>
        </Stack>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              <CalculateIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 18 }} />
              Currently effective values
            </Typography>
            <Box component="pre" sx={{
              fontFamily: 'monospace', fontSize: 12, m: 0,
              bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
              p: 1.5, borderRadius: 1, overflow: 'auto',
            }}>
{`CDN URL:        ${form.sdkCdnUrl || sameOriginCdn}
Beacon URL:     ${form.beaconEndpoint || sameOriginBeacon}
SRI Hash:       ${form.sdkSriHash || '(none — script tag will not include integrity attribute)'}
SDK Version:    ${form.sdkVersion || 'v2.0.0'}`}
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
